import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { newDraftId } from '../components/drafts/draftModel';

/**
 * Where the scratchpad is, and which drafts it's holding.
 *
 * Lives above the router outlet so the dock survives navigation: opening a draft
 * on Home and walking to Tasks must not lose it. Small enough to keep the
 * documents themselves out — the dock owns the draft bodies, this owns only the
 * few facts that have to outlive a route change and a reload.
 *
 * ── The inline slot ──────────────────────────────────────────────────────────
 * A route can offer the scratchpad a place to live inside its own layout — the
 * project console does, as one of its panes. It registers a DOM node here and
 * the dock portals into it instead of floating. The dock component itself is
 * unmoved in the React tree, so the whole document, its caret and its pending
 * autosave survive the switch; only the DOM parent changes.
 *
 * ── One seat or two ──────────────────────────────────────────────────────────
 * `draftIds` is `[id]` or `[id, id]`, and its *length* is the split. Two fields
 * (`activeDraftId` + `secondDraftId`) would have read more plainly, but every
 * operation here is symmetric — assign, swap, close, promote the survivor — and
 * writing each of them twice is how the two halves drift apart.
 *
 * A `null` in the second seat means "split requested, occupant not yet decided".
 * Only the dock can decide: picking a draft needs the file list, which lives in
 * React Query, one provider down. This is the same shape slot 0 has always had —
 * `open()` with no argument leaves a null the dock resolves to the most recent
 * note — so the resolution path is one path, not a new one.
 */

const DraftDockContext = createContext(null);

const STORAGE_VERSION = 1;
const storageKey = (projectId) => `forge-ai:drafts:v${STORAGE_VERSION}:${projectId}`;

const CLOSED = { isOpen: false, isCollapsed: false, draftIds: [null] };

/**
 * The version is deliberately *not* bumped for `draftIds`.
 *
 * An entry written before split existed carries `activeDraftId` and no
 * `draftIds`, and `[activeDraftId]` is exactly what it meant — so the old shape
 * reads correctly rather than being discarded, and nobody's open draft closes
 * itself on the deploy that ships this.
 */
function readStored(projectId) {
    if (!projectId || typeof window === 'undefined') return CLOSED;
    try {
        const raw = window.localStorage.getItem(storageKey(projectId));
        if (!raw) return CLOSED;
        const parsed = JSON.parse(raw);
        const stored = Array.isArray(parsed.draftIds) ? parsed.draftIds.slice(0, 2) : null;
        return {
            isOpen: !!parsed.isOpen,
            isCollapsed: !!parsed.isCollapsed,
            draftIds: stored?.length ? stored : [parsed.activeDraftId ?? null],
        };
    } catch {
        // A corrupt entry is not worth a broken route.
        return CLOSED;
    }
}

export function DraftDockProvider({ projectId, readOnly, children }) {
    // Lazy initialiser: reading localStorage on every render of a provider this
    // high in the tree would be a synchronous disk hit per keystroke below it.
    const [state, setState] = useState(() => (readOnly ? CLOSED : readStored(projectId)));

    /**
     * How many columns the draft in the first seat is currently divided into.
     *
     * Here rather than in the dock because the *console* is what reacts to it —
     * a two-column note gets a wider pane — and the console is a sibling of the
     * dock, not an ancestor. Deliberately outside `state`, so it never reaches
     * localStorage: the count is a property of the document and is re-derived
     * from its body on load. Persisting it would only create a second copy to
     * disagree with.
     */
    const [columnCount, setColumnCount] = useState(1);

    // Switching projects without unmounting the provider (the id is a route
    // param) has to load that project's dock, not carry this one across.
    useEffect(() => {
        setState(readOnly ? CLOSED : readStored(projectId));
        setColumnCount(1);
    }, [projectId, readOnly]);

    useEffect(() => {
        if (!projectId || readOnly || typeof window === 'undefined') return;
        try {
            window.localStorage.setItem(storageKey(projectId), JSON.stringify(state));
        } catch {
            // Private mode, quota, disabled storage — the dock still works for
            // this session, it just won't come back after a reload.
        }
    }, [projectId, readOnly, state]);

    /**
     * A place inside the current route's layout for the scratchpad to render.
     * Every route but the console leaves both of these alone and gets a floating
     * panel — as does the console below `md`, where a pane in a scrolling stack
     * is a much worse home for an editor than a bottom sheet.
     *
     * Two fields, not one, and the reason is worth stating. `wanted` is declared
     * by the route the moment it mounts; `el` only exists once that route has
     * finished loading and painted the pane. Without the declaration the dock
     * would mount into a floating panel during the skeleton and then move into
     * the pane a moment later — and moving between frames re-mounts the editor,
     * so the caret you were promised would land and then vanish. `wanted`
     * without `el` means "hold on", which costs one commit and no flicker.
     */
    const [inline, setInline] = useState({ wanted: false, el: null });

    const declareInlineSlot = useCallback(
        (wanted) => setInline((prev) => (prev.wanted === wanted ? prev : { ...prev, wanted })),
        []
    );
    const setInlineSlot = useCallback(
        (el) => setInline((prev) => (prev.el === (el ?? null) ? prev : { ...prev, el: el ?? null })),
        []
    );

    const open = useCallback((draftId = null) => {
        setState((prev) => ({
            ...prev,
            isOpen: true,
            isCollapsed: false,
            // `open()` with no argument means "whatever was there" — the dock
            // resolves a null id to the most recent draft, or creates one.
            draftIds: [draftId ?? prev.draftIds[0], ...prev.draftIds.slice(1)],
        }));
    }, []);

    const close = useCallback(() => setState((prev) => ({ ...prev, isOpen: false, isCollapsed: false })), []);

    const collapse = useCallback(() => setState((prev) => ({ ...prev, isCollapsed: true })), []);

    const expand = useCallback(() => setState((prev) => ({ ...prev, isOpen: true, isCollapsed: false })), []);

    /**
     * What the shortcut and the bar button do: a blank draft, every time.
     *
     * Not a toggle. Summoning a capture tool has to mean "somewhere to write
     * this thought", and a key that sometimes reopens yesterday's note and
     * sometimes puts the panel away is a key you have to look at the screen to
     * use. Esc and ✕ close it; the switcher is how you get back to an old one.
     *
     * A fresh id even when a blank draft is already on screen, so the dock
     * reloads and lands the caret — pressing `q` after clicking away should put
     * you back in the note, not do nothing.
     *
     * The split survives: `q` asks for somewhere to write, not for the layout to
     * change under you. The blank lands in the first seat and the second keeps
     * whatever you were reading beside it.
     */
    const requestNew = useCallback(() => {
        setState((prev) => ({
            isOpen: true,
            isCollapsed: false,
            draftIds: [newDraftId(), ...prev.draftIds.slice(1)],
        }));
    }, []);

    /**
     * Point one seat at a different draft without touching whether the dock is
     * open. Switching drafts from the header must not un-collapse it, and the
     * dock adopts the most recent draft on load — which would pop the panel open
     * on every page load if this forced `isOpen`.
     *
     * Asking for a draft that is already in the other seat swaps the two rather
     * than showing it twice. Two panes editing one document would be two
     * autosaves racing over the same row.
     */
    const setPaneDraftId = useCallback((slot, id) => {
        setState((prev) => {
            const ids = prev.draftIds;
            if (ids[slot] === id) return prev;
            const other = slot === 0 ? 1 : 0;
            const next = ids.slice();
            if (ids.length === 2 && ids[other] === id) next[other] = ids[slot];
            next[slot] = id;
            return { ...prev, draftIds: next };
        });
    }, []);

    /** Kept for the callers that only ever meant the first seat. */
    const setActiveDraftId = useCallback((id) => setPaneDraftId(0, id), [setPaneDraftId]);

    /** Put a specific draft in the other seat, opening the split if needed. */
    const openBeside = useCallback((id) => {
        setState((prev) => {
            const [first, second] = prev.draftIds;
            if (second === id) return prev;
            return {
                isOpen: true,
                isCollapsed: false,
                // "Beside" the one you are already reading can only mean "give it
                // a neighbour" — so leave the second seat null and let the dock
                // choose who, exactly as it chooses the first seat's occupant.
                draftIds: [first, first === id ? (second ?? null) : id],
            };
        });
    }, []);

    const toggleSplit = useCallback(() => {
        setState((prev) => (prev.draftIds.length === 2
            ? { ...prev, isOpen: true, isCollapsed: false, draftIds: [prev.draftIds[0]] }
            : { isOpen: true, isCollapsed: false, draftIds: [prev.draftIds[0] ?? null, null] }));
    }, []);

    /**
     * Close one seat. With two open that leaves the other one holding the dock;
     * with one, there is nothing left to hold it, so the dock closes — which is
     * what makes a single ✕ mean the right thing in both layouts.
     */
    const closePane = useCallback((slot) => {
        setState((prev) => {
            if (prev.draftIds.length < 2) return { ...prev, isOpen: false, isCollapsed: false };
            return { ...prev, draftIds: [prev.draftIds[slot === 0 ? 1 : 0]] };
        });
    }, []);

    const value = useMemo(() => ({
        ...state,
        // The first seat, under its old name. Components that never knew about
        // split — the file panel's "open this in the dock" — still read this.
        activeDraftId: state.draftIds[0],
        isSplit: state.draftIds.length === 2,
        columnCount,
        setColumnCount,
        // Every consumer checks this rather than each call site remembering to.
        // A share-link visitor has no project to write to.
        enabled: !readOnly && !!projectId,
        projectId,
        inlineSlot: inline.el,
        inlineWanted: inline.wanted,
        open,
        close,
        collapse,
        expand,
        requestNew,
        setActiveDraftId,
        setPaneDraftId,
        openBeside,
        toggleSplit,
        closePane,
        declareInlineSlot,
        setInlineSlot,
    }), [state, columnCount, readOnly, projectId, inline, open, close, collapse, expand, requestNew,
        setActiveDraftId, setPaneDraftId, openBeside, toggleSplit, closePane, declareInlineSlot, setInlineSlot]);

    return <DraftDockContext.Provider value={value}>{children}</DraftDockContext.Provider>;
}

/**
 * Returns null outside a project, so components shared between the project shell
 * and the dashboard don't have to care.
 */
export function useDraftDock() {
    return useContext(DraftDockContext);
}

export default DraftDockProvider;
